import { useEffect, useState } from "react";
import api, { eur, fmtDate } from "@/lib/apiClient";
import { logoSrc } from "@/components/BrandLogo";

const Row = ({ label, children, id }) => (
  <div className="flex justify-between gap-4 py-1.5 border-b border-zinc-100 text-sm">
    <span className="text-zinc-500">{label}</span><span data-testid={id} className="text-right font-medium">{children || "—"}</span>
  </div>
);

export const ExpenseDetail = ({ id }) => {
  const [x, setX] = useState(null);
  useEffect(() => { if (id) { api.get(`/expenses/${id}`).then((r) => setX(r.data)).catch(() => setX(false)); } }, [id]);
  if (x === null) return <div className="text-sm text-zinc-400">Loading expense…</div>;
  if (x === false) return <div className="text-sm text-zinc-400">Expense details are no longer available.</div>;
  const src = x.file_url ? logoSrc(x.file_url) : null;
  const isPdf = /\.pdf($|\?)/i.test(x.file_url || "");
  return (
    <div data-testid="expense-detail">
      {x.duplicates?.length > 0 && (
        <div data-testid="expense-dup-warning" className="mb-3 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          <div className="font-semibold">Possible duplicate receipt</div>
          <div className="text-xs mt-0.5">Same supplier, amount and date as:</div>
          <ul className="text-xs mt-1 space-y-0.5">{x.duplicates.map((d) => <li key={d.id} data-testid={`expense-dup-${d.id}`}>{d.employee_name} · {d.site_name || "—"} · submitted {fmtDate(d.date)} · <span className="capitalize">{d.status}</span></li>)}</ul>
        </div>
      )}
      <Row label="Submitted by" id="expense-detail-employee">{x.employee_name}</Row>
      <Row label="Date" id="expense-detail-date">{fmtDate(x.date)}</Row>
      {x.receipt_date && <Row label="Receipt date" id="expense-detail-receipt-date">{fmtDate(x.receipt_date)}</Row>}
      <Row label="Site / job" id="expense-detail-site">{x.site_name}</Row>
      <Row label="Description" id="expense-detail-desc">{x.description}</Row>
      <Row label="Supplier" id="expense-detail-supplier">{x.supplier}</Row>
      <Row label="Amount (inc VAT)" id="expense-detail-amount">{eur(x.amount)}</Row>
      <Row label="VAT rate" id="expense-detail-vat">{x.vat_rate != null ? `${x.vat_rate}%` : "Not shown"}</Row>
      {x.read_by_ai && <Row label="Details" id="expense-detail-ai">Read from receipt, checked by staff</Row>}
      <Row label="Status" id="expense-detail-status"><span className="capitalize">{x.status}</span></Row>
      {x.reviewed_by && <Row label="Reviewed" id="expense-detail-reviewed">{x.reviewed_by}{x.reviewed_at ? ` · ${fmtDate(x.reviewed_at.slice(0, 10))}` : ""}</Row>}
      {x.reject_reason && <Row label="Reject reason" id="expense-detail-reason">{x.reject_reason}</Row>}
      <div className="mt-3">
        <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Receipt</div>
        {!src ? <div className="text-sm text-zinc-400" data-testid="expense-detail-no-receipt">No receipt attached</div>
          : isPdf ? <a data-testid="expense-detail-receipt-link" href={src} target="_blank" rel="noreferrer" className="text-amber-700 underline text-sm">Open receipt (PDF)</a>
          : <a href={src} target="_blank" rel="noreferrer"><img data-testid="expense-detail-receipt" src={src} alt="Receipt" className="max-h-80 rounded border border-zinc-200 object-contain" /></a>}
      </div>
    </div>
  );
};
